import classNames from 'classnames';
import moment from 'moment';
import React, { useReducer } from 'react';
import { useSelector } from 'react-redux';

import { FileObject } from '../dff-analyse/dff-analyse';
import * as selectors from '../dff/dff-selectors';
import { sizeDisplay, PathUtils } from '../utils';

import './tree.scss';

export interface TreeProps {
  file?: FileObject;
}

const initialState = {
  tree: {},
  isOpen: false,
}

function reducer(state, action) {
  switch (action.type) {
    case 'toggleFileOpen':
      return { ...initialState, isOpen: action.payload };
    default:
      return state;
  }
}

export const Tree: React.FC<TreeProps> = ({ file }) => {
  const [, dispatch] = useReducer(reducer, initialState);

  const filePercentage = (file: FileObject): number => {
    return (file.size / file.parent.size) * 100;
  };

  const isDir = (file: FileObject): boolean => {
    return file.file.type === 'dir';
  };

  const toggle = (e: React.MouseEvent<HTMLAnchorElement>, file: FileObject) => {
    e.preventDefault();
    file.isOpen = !file.isOpen;
    dispatch({ type: 'toggleFileOpen', payload: !file.isOpen });
  };

  const baseUrl = useSelector(selectors.baseUrl);

  const originalUrl = (file: FileObject): string => {
    const parentPath = PathUtils.parent(file.path);
    const name = PathUtils.baseName(file.path);

    return `${baseUrl}/app/storage/${file.mountId}?path=${encodeURIComponent(parentPath)}&name=${encodeURIComponent(name)}`;
  };

  return (
    <table className="tree">
      <tbody>
        {
          file.children.map((f, index) => (
            <tr className="tree-item clearfix" key={index}>
              <td>
                <table>
                  <tbody>
                    <tr>
                      <td className="col-name">
                        {
                          isDir(f) ? (
                            <div>
                              <a href="#" onClick={(e) => toggle(e, f)}>
                                <span className="tree-item-type-dir">
                                  <div className={classNames('arrow', {'arrow--is-open': f.isOpen} )}></div>
                                  {f.file.name}
                                </span>
                              </a>
                            </div>
                          ) : (
                              <div>
                                <a href={originalUrl(f)} className="tree-item-type-file" target="_blank">
                                  <div className="dot"></div>
                                  {f.file.name}
                                </a>
                              </div>
                            )
                        }
                      </td>

                      <td className="col-space">
                        <div className="progress tree-item-progress">
                          <div className={classNames('progress-bar', { blue : filePercentage(f) > 50, red : filePercentage(f) > 90 })} style={{ width: `${filePercentage(f)}%` }}></div>
                        </div>
                      </td>

                      <td className="tree-item-size col-size">
                        <span className={classNames({ 'tree-item-loading': f.remaining > 0, 'tree-item-loaded': f.remaining === 0 })}>{sizeDisplay(f.size)}</span>
                      </td>

                      <td className="tree-item-files col-files">
                        {
                          isDir(f) ? (
                            <span className={classNames({ 'tree-item-loading': f.remaining > 0, 'tree-item-loaded': f.remaining === 0 })}>{ f.descendants }</span>
                          ) : null
                        }
                      </td>

                      <td className="tree-item-size col-modified">
                        <span className={classNames({ 'tree-item-loading': f.remaining > 0, 'tree-item-loaded': f.remaining === 0 })}>{moment(f.modified).format('lll')}</span>
                      </td>
                      
                    </tr>
                    <tr>
                      <td colSpan={5} className="add-inline">
                        {
                          isDir(f) && f.isOpen ? (
                            <Tree file={f} ></Tree>
                          ) : null
                        }
                      </td>
                    </tr>
                  </tbody>

                </table>
              </td>
            </tr>
          ))
        }
      </tbody>

    </table>
  );
};
